import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Search, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";

function Selected({ item, onChange, onDelete, saving }) {
  return (
    <div data-testid="media-detail" className="space-y-4">
      <div className="aspect-[4/3] bg-mist/40 overflow-hidden">
        <img src={mediaUrl(item.files?.medium || item.files?.original)} alt={item.alt_text || item.original_name} className="w-full h-full object-contain" />
      </div>
      <div className="text-[12px] text-ink/55 grid grid-cols-2 gap-2">
        <span>File: <span className="text-ink/80">{item.original_name}</span></span>
        <span>Dimensions: <span className="text-ink/80">{item.width}×{item.height}</span></span>
        <span>Size: <span className="text-ink/80">{(item.size_bytes / 1024).toFixed(0)} KB</span></span>
        <span>Uploaded: <span className="text-ink/80">{new Date(item.uploaded_at).toLocaleString()}</span></span>
      </div>
      <div>
        <label className="eyebrow text-ink/55">Alt text (required for accessibility)</label>
        <input data-testid="media-alt" value={item.alt_text || ""} onChange={(e) => onChange({ ...item, alt_text: e.target.value })} className="input-line mt-3" />
      </div>
      <div>
        <label className="eyebrow text-ink/55">Caption</label>
        <input data-testid="media-caption" value={item.caption || ""} onChange={(e) => onChange({ ...item, caption: e.target.value })} className="input-line mt-3" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="eyebrow text-ink/55">Credit</label>
          <input data-testid="media-credit" value={item.credit || ""} onChange={(e) => onChange({ ...item, credit: e.target.value })} className="input-line mt-3" />
        </div>
        <div>
          <label className="eyebrow text-ink/55">Source / license</label>
          <input data-testid="media-license" value={item.source_license || ""} onChange={(e) => onChange({ ...item, source_license: e.target.value })} className="input-line mt-3" placeholder="e.g. Unsplash, CC BY-2.0, Owned" />
        </div>
      </div>
      <div>
        <label className="eyebrow text-ink/55">Internal notes</label>
        <textarea data-testid="media-notes" value={item.notes || ""} onChange={(e) => onChange({ ...item, notes: e.target.value })} rows={2} className="input-line mt-3 resize-y" />
      </div>
      <div className="flex items-center justify-between pt-2">
        <button type="button" onClick={onDelete} data-testid="media-delete" className="text-[12px] tracking-[0.14em] uppercase text-ink/55 hover:text-copper inline-flex items-center gap-2">
          <Trash2 size={14} /> Delete image
        </button>
        <button
          type="button"
          onClick={() => onChange(item, true)}
          disabled={saving}
          data-testid="media-save"
          className="btn-primary text-[12px] py-2 px-5 inline-flex items-center gap-2 disabled:opacity-40"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save details"}
        </button>
      </div>
    </div>
  );
}

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = async (query = "") => {
    const r = await api.get("/admin/media", { params: query ? { q: query } : {} });
    setItems(r.data || []);
  };

  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/media", fd);
      toast.success("Uploaded.");
      setItems((prev) => [r.data, ...prev]);
      setSelected(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onChange = async (next, save = false) => {
    setSelected(next);
    if (!save) return;
    setSaving(true);
    try {
      const r = await api.put(`/admin/media/${next.id}`, {
        alt_text: next.alt_text || "",
        caption: next.caption || "",
        credit: next.credit || "",
        source_license: next.source_license || "",
        notes: next.notes || "",
      });
      setSelected(r.data);
      setItems((prev) => prev.map((x) => (x.id === r.data.id ? r.data : x)));
      toast.success("Saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.original_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/media/${selected.id}`);
      setItems((prev) => prev.filter((x) => x.id !== selected.id));
      setSelected(null);
      toast.success("Deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-3 font-serif text-4xl text-jade font-light">Media library</h1>
          <p className="mt-3 text-ink/65 text-[14px] font-light max-w-xl">
            JPG, PNG and WEBP only. Max 8 MB per upload. Three optimised sizes (thumb, medium, large) are generated automatically. EXIF metadata is stripped on upload.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" onChange={onUpload} className="hidden" data-testid="media-file" />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            data-testid="media-upload"
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload image"}
          </button>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-10">
        {/* Grid */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 border-b border-mist pb-2 mb-6">
            <Search size={14} className="text-ink/50" />
            <input
              data-testid="media-search"
              type="text"
              value={q}
              onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
              placeholder="Search by title, alt text, caption, credit, notes…"
              className="flex-1 bg-transparent outline-none text-[14px]"
            />
          </div>
          {items.length === 0 ? (
            <p className="text-ink/60 text-sm italic">No images yet. Upload one to get started.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {items.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`media-item-${m.id}`}
                  onClick={() => setSelected(m)}
                  className={`text-left border ${selected?.id === m.id ? "border-jade" : "border-transparent hover:border-jade"} transition-colors`}
                >
                  <div className="aspect-square bg-mist/40 overflow-hidden">
                    <img src={mediaUrl(m.files?.thumb)} alt={m.alt_text || m.original_name} className="w-full h-full object-cover" />
                  </div>
                  <p className="mt-2 text-[12px] text-ink/75 truncate">{m.alt_text || m.original_name}</p>
                  <p className="text-[10px] tracking-[0.14em] uppercase text-ink/40">{m.width}×{m.height}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-5">
          {selected ? (
            <Selected item={selected} onChange={onChange} onDelete={onDelete} saving={saving} />
          ) : (
            <div className="border border-mist p-6 text-ink/60 text-[14px] italic">
              Select an image on the left to edit its alt text, caption, credit, license and internal notes.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
